/* eslint-disable func-names */
/* eslint-disable no-nested-ternary */
import omit from 'ramda/src/omit';
import prop from 'ramda/src/prop';
import assoc from 'ramda/src/assoc';
import compose from 'ramda/src/compose';
import find from 'ramda/src/find';
import flatten from 'ramda/src/flatten';
import map from 'ramda/src/map';
import propEq from 'ramda/src/propEq';
import reject from 'ramda/src/reject';
import { LICENSOR_TYPES, MFN_TYPES } from 'utils/constants';
import { getAllClearancesCall } from 'redux-core/clearances/services';
import { rootTranslation } from 'utils/format';
import * as Yup from 'yup';

const validateRightsOwnerInTrack = async ({ licensorId, trackId, qTracksRightsOwnerId }) => {
  const clearances = await getAllClearancesCall({ trackId });
  return compose(
    Boolean,
    find(propEq('qwireTracksRightsOwnerId', qTracksRightsOwnerId)),
    reject(propEq('id', licensorId)),
    flatten,
    map(prop('licensors')),
  )(clearances);
};

export const getPayload = ({ values, type, isEditing, trackId, originalType, divisionId, licensorsExist }) => {
  const { licensor } = values;
  const payload = {
    ...omit(['qTrack'], licensor),
    divisionId,
    type: isEditing
      ? originalType
      : type === LICENSOR_TYPES.ONE_STOP
      ? LICENSOR_TYPES.SYNC // one_stop always should be treated as sync
      : type,
    name: prop('name', licensor.qTrack),
    mfn: licensorsExist ? MFN_TYPES.MFN_NONE : MFN_TYPES.MFN_BASE,
    qwireTracksRightsOwnerId: prop('qwireTracksRightsOwnerId', licensor.qTrack),
  };
  if (!isEditing) return assoc('qclearTrackId', trackId, payload);
  return payload;
};

/**
 * A refused save carries a message meant for the user, e.g. the server refusing a licensor
 * that is already on the track. Anything else falls back to the generic error.
 */
export const showLicensorSaveError = ({ error, showSnackbar, showGenericError }) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;
  if (status >= 400 && status < 500 && typeof message === 'string') return showSnackbar({ message });
  return showGenericError(error);
};

const t = rootTranslation('drawers.licensorsAddEdit.basicInfo');
const getValidationSchema = (schema) =>
  /**
   *
   * This closure is necessary to avoid running the async Yup validation
   * on every form change. We need to remember the last checked value,
   * so that we only fetch the backend to perform the validation when the
   * rights owner id actually changes.
   */
  function ({ initialRightsOwnerId, licensorId, trackId }) {
    let prevRightsOwnerId = initialRightsOwnerId;
    let hasError = false;
    let generation = 0;

    const buildSchema = () =>
      Yup.object().shape({
        duplicatedLicensor: Yup.string()
          .nullable()
          .test('duplicatedLicensor', '', async function () {
            const qTracksRightsOwnerId = this.parent.licensor.qTrack.qwireTracksRightsOwnerId;

            const setError = () =>
              this.createError({
                path: this.path,
                message: t('repeatedLicensor'),
              });

            // If the qTracksRightsOwnerId didn't change, then we keep the
            // same error status we had before
            if (qTracksRightsOwnerId === prevRightsOwnerId || qTracksRightsOwnerId === initialRightsOwnerId) {
              if (!hasError) return true;
              return setError();
            }
            // If qTracksRightsOwnerId, perform validation
            const checkedGeneration = generation;
            const isOnTrack = await validateRightsOwnerInTrack({
              licensorId,
              trackId,
              qTracksRightsOwnerId,
            });
            // An answer fetched before the track changed is not remembered
            if (checkedGeneration === generation) {
              hasError = isOnTrack;
              prevRightsOwnerId = qTracksRightsOwnerId;
            }

            if (!isOnTrack) return true;
            return setError();
          }),
        ...schema,
      });

    // Save and Add New puts a licensor on the track and keeps the drawer open, so the answer
    // remembered from before that save no longer holds
    buildSchema.forgetCheckedLicensor = () => {
      prevRightsOwnerId = initialRightsOwnerId;
      hasError = false;
      generation += 1;
    };

    return buildSchema;
  };

export const getMasterValidationSchema = getValidationSchema({
  licensor: Yup.object().shape({
    contact: Yup.object().shape({
      email: Yup.string().nullable().email('global.forms.validations.types.email'),
    }),
    qTrack: Yup.object().shape({
      name: Yup.string().required('global.forms.validations.required'),
    }),
    recordLabels: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('global.forms.validations.required'),
        contact: Yup.object().shape({
          email: Yup.string().nullable().email('global.forms.validations.types.email'),
        }),
      }),
    ),
  }),
});
